import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { useAppState } from "../context/AppStateContext";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { useToast } from "../context/ToastContext";
import {
  districtDisplayLabel,
  districtSelectOptionsFromStore,
  mandalSelectOptionsFromStore,
  villageSelectOptionsFromStore,
} from "../utils/geoDirectorySelectOptions";
import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import type { GeoZone, GeoZoneMandalRef, GeoZoneVillageRef, StoreYard, StoreYardScopeType } from "../types";
import { mandalRefKey, villageRefKey } from "../utils/geoZones";
import { applicationMatchesStoreYard } from "../utils/storeYards";

function districtLabel(geoStore: GeoDirectoryStore | null, key: string): string {
  return districtDisplayLabel(geoStore, key);
}

const SCOPE_OPTIONS: { value: StoreYardScopeType; label: string }[] = [
  { value: "district", label: "Whole district(s)" },
  { value: "mandal", label: "Specific mandal(s)" },
  { value: "village", label: "Specific village(s)" },
  { value: "zone", label: "Geographic zone(s) from master" },
];

function addStoreCoverageValid(
  scopeType: StoreYardScopeType,
  districtKeys: string[],
  mandalRefs: GeoZoneMandalRef[],
  villageRefs: GeoZoneVillageRef[],
  zoneIds: string[],
): boolean {
  switch (scopeType) {
    case "district":
      return districtKeys.length > 0;
    case "mandal":
      return mandalRefs.length > 0;
    case "village":
      return villageRefs.length > 0;
    case "zone":
      return zoneIds.length > 0;
    default:
      return false;
  }
}

function storeYardCoverageStats(s: StoreYard): string {
  switch (s.scopeType) {
    case "district":
      return `${s.districtKeys.length} district${s.districtKeys.length === 1 ? "" : "s"}`;
    case "mandal":
      return `${s.mandalRefs.length} mandal${s.mandalRefs.length === 1 ? "" : "s"}`;
    case "village":
      return `${s.villageRefs.length} village${s.villageRefs.length === 1 ? "" : "s"}`;
    case "zone":
      return `${s.zoneIds.length} zone${s.zoneIds.length === 1 ? "" : "s"}`;
    default:
      return "—";
  }
}

function StoreYardSummaryCard({
  store: s,
  storeIndex,
  isActive,
  onOpen,
}: {
  store: StoreYard;
  storeIndex: number;
  isActive: boolean;
  onOpen: () => void;
}) {
  const scopeLabel = SCOPE_OPTIONS.find((o) => o.value === s.scopeType)?.label ?? s.scopeType;
  const note = s.notes.trim();
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Store {storeIndex + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{s.name}</span>
        <span className="milestone-master-picker-card__stats" aria-label="Coverage summary">
          <span>{scopeLabel}</span>
          <span className="milestone-master-picker-card__stat-dot" aria-hidden>
            ·
          </span>
          <span>{storeYardCoverageStats(s)}</span>
        </span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function AddStoreYardForm({ geoZones, onCreated }: { geoZones: GeoZone[]; onCreated: () => void }) {
  const { addStoreYard, updateStoreYard } = useAppState();
  const { showToast } = useToast();
  const { store: geoStore } = useGeoDirectory();

  const [createName, setCreateName] = useState("");
  const [createNotes, setCreateNotes] = useState("");
  const [createAddress, setCreateAddress] = useState("");
  const [createScopeType, setCreateScopeType] = useState<StoreYardScopeType>("district");
  const [createDistrictKeys, setCreateDistrictKeys] = useState<string[]>([]);
  const [createMandalRefs, setCreateMandalRefs] = useState<GeoZoneMandalRef[]>([]);
  const [createVillageRefs, setCreateVillageRefs] = useState<GeoZoneVillageRef[]>([]);
  const [createZoneIds, setCreateZoneIds] = useState<string[]>([]);
  const [createDistrictPickKey, setCreateDistrictPickKey] = useState("");

  const [mandalDistrict, setMandalDistrict] = useState("");
  const [mandalName, setMandalName] = useState("");
  const [villageDistrict, setVillageDistrict] = useState("");
  const [villageMandal, setVillageMandal] = useState("");
  const [villageName, setVillageName] = useState("");

  useEffect(() => {
    if (!geoStore) return;
    const first = geoStore.tgRegistrationDistricts()[0]?.districtKey ?? "";
    setMandalDistrict((prev) => prev || first);
    setVillageDistrict((prev) => prev || first);
  }, [geoStore]);

  const mandalsForPick = useMemo(
    () => (geoStore ? geoStore.tgMandalsForDistrict(mandalDistrict) : []),
    [geoStore, mandalDistrict],
  );
  const villagesForPick = useMemo(() => {
    if (!geoStore || !villageMandal.trim()) return [];
    return geoStore.tgVillagesForMandal(villageDistrict, villageMandal);
  }, [geoStore, villageDistrict, villageMandal]);

  const sortedDistricts = useMemo(() => districtSelectOptionsFromStore(geoStore), [geoStore]);

  const districtPickOptions = sortedDistricts;

  const districtAddPickOptions = useMemo(() => {
    const avail = sortedDistricts.filter((d) => !createDistrictKeys.includes(d.value));
    return [{ value: "", label: "— Choose district —", rowKey: "none" }, ...avail];
  }, [sortedDistricts, createDistrictKeys]);

  const mandalPickOptions = useMemo(() => {
    const sorted = mandalSelectOptionsFromStore(geoStore, mandalDistrict);
    return [{ value: "", label: "— Choose mandal —", rowKey: "none" }, ...sorted];
  }, [geoStore, mandalDistrict]);

  const villageMandalPickOptions = useMemo(() => {
    const sorted = mandalSelectOptionsFromStore(geoStore, villageDistrict);
    return [{ value: "", label: "— Choose mandal —", rowKey: "none" }, ...sorted];
  }, [villageDistrict, geoStore]);

  const villageNamePickOptions = useMemo(() => {
    const sorted = villageSelectOptionsFromStore(geoStore, villageDistrict, villageMandal);
    return [{ value: "", label: "— Choose village —", rowKey: "none" }, ...sorted];
  }, [geoStore, villageDistrict, villageMandal]);

  const sortedZones = useMemo(
    () => [...geoZones].sort((a, b) => a.sortOrder - b.sortOrder),
    [geoZones],
  );

  function resetCreateGeo() {
    setCreateDistrictKeys([]);
    setCreateMandalRefs([]);
    setCreateVillageRefs([]);
    setCreateZoneIds([]);
    setCreateDistrictPickKey("");
    setMandalName("");
    setVillageMandal("");
    setVillageName("");
  }

  function onCreateScopeTypeChange(next: StoreYardScopeType) {
    setCreateScopeType(next);
    resetCreateGeo();
  }

  function addCreateMandal() {
    const mn = mandalName.trim();
    if (!mn) return;
    const ref: GeoZoneMandalRef = { district: mandalDistrict, mandalName: mn };
    const k = mandalRefKey(ref.district, ref.mandalName);
    setCreateMandalRefs((prev) => {
      if (prev.some((r) => mandalRefKey(r.district, r.mandalName) === k)) return prev;
      return [...prev, ref];
    });
    setMandalName("");
  }

  function removeCreateMandal(ref: GeoZoneMandalRef) {
    setCreateMandalRefs((prev) =>
      prev.filter((r) => mandalRefKey(r.district, r.mandalName) !== mandalRefKey(ref.district, ref.mandalName)),
    );
  }

  function addCreateVillage() {
    const mn = villageMandal.trim();
    const vn = villageName.trim();
    if (!mn || !vn) return;
    const ref: GeoZoneVillageRef = { district: villageDistrict, mandalName: mn, villageName: vn };
    const k = villageRefKey(ref.district, ref.mandalName, ref.villageName);
    setCreateVillageRefs((prev) => {
      if (prev.some((r) => villageRefKey(r.district, r.mandalName, r.villageName) === k)) return prev;
      return [...prev, ref];
    });
    setVillageName("");
  }

  function removeCreateVillage(ref: GeoZoneVillageRef) {
    setCreateVillageRefs((prev) =>
      prev.filter(
        (r) =>
          villageRefKey(r.district, r.mandalName, r.villageName) !==
          villageRefKey(ref.district, ref.mandalName, ref.villageName),
      ),
    );
  }

  function toggleCreateZone(zoneId: string) {
    setCreateZoneIds((prev) => (prev.includes(zoneId) ? prev.filter((id) => id !== zoneId) : [...prev, zoneId]));
  }

  /** District picked in the dropdown counts toward coverage even before "Add district". */
  const effectiveDistrictKeysForCreate = useMemo(() => {
    if (createScopeType !== "district") return createDistrictKeys;
    const k = createDistrictPickKey;
    if (k && !createDistrictKeys.includes(k)) {
      return [...createDistrictKeys, k];
    }
    return createDistrictKeys;
  }, [createScopeType, createDistrictKeys, createDistrictPickKey]);

  const coverageOk = addStoreCoverageValid(
    createScopeType,
    effectiveDistrictKeysForCreate,
    createMandalRefs,
    createVillageRefs,
    createZoneIds,
  );

  return (
    <>
      <div className="field">
        <label htmlFor="new-sy-name">Name</label>
        <input
          id="new-sy-name"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          placeholder="e.g. District warehouse — Karimnagar"
          maxLength={200}
        />
      </div>
      <div className="field">
        <label htmlFor="new-sy-notes">Notes (optional)</label>
        <textarea
          id="new-sy-notes"
          rows={2}
          value={createNotes}
          onChange={(e) => setCreateNotes(e.target.value)}
          maxLength={2000}
        />
      </div>
      <div className="field">
        <label htmlFor="new-sy-address">Shipping address (for PO print)</label>
        <textarea
          id="new-sy-address"
          rows={3}
          value={createAddress}
          onChange={(e) => setCreateAddress(e.target.value)}
          placeholder="Full postal / site address shown on PO when this store is ship-to"
          maxLength={4000}
        />
      </div>

      <h3 className="form-section-title" style={{ marginTop: "0.5rem" }}>Geographic coverage</h3>
      <p className="section-lead section-lead--tight text-muted">
        Pick how this depot is tied to location. You can change this later on the store card.
      </p>
      <div className="field">
        <label htmlFor="new-sy-scope">Coverage type</label>
        <select
          id="new-sy-scope"
          value={createScopeType}
          onChange={(e) => onCreateScopeTypeChange(e.target.value as StoreYardScopeType)}
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {createScopeType === "district" ? (
        <>
          <p className="section-lead section-lead--tight text-muted" style={{ marginTop: "0.85rem" }}>
            Add every district this depot covers (multiselect). Choose a district, click Add district, repeat. Remove any row with Remove.
          </p>
          <div className="geo-zones-add-row">
            <div className="field field-inline" style={{ flex: "1 1 14rem" }}>
              <label htmlFor="new-sy-district-pick">District</label>
              <SearchableSelect
                id="new-sy-district-pick"
                variant="dash"
                value={createDistrictPickKey}
                onChange={(v) => setCreateDistrictPickKey(v)}
                options={districtAddPickOptions}
                placeholder="— Choose district —"
              />
            </div>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              disabled={
                !createDistrictPickKey ||
                !createDistrictPickKey ||
                createDistrictKeys.includes(createDistrictPickKey)
              }
              onClick={() => {
                const k = createDistrictPickKey;
                if (!k || createDistrictKeys.includes(k)) return;
                setCreateDistrictKeys((prev) => [...prev, k]);
                setCreateDistrictPickKey("");
              }}
            >
              Add district
            </button>
          </div>
          {createDistrictKeys.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {[...createDistrictKeys]
                .sort((a, b) => districtLabel(geoStore, a).localeCompare(districtLabel(geoStore, b)))
                .map((key) => (
                  <li key={key} className="geo-zones-pill-row">
                    <span>{districtLabel(geoStore, key)}</span>
                    <button
                      type="button"
                      className="btn btn--link btn--sm"
                      onClick={() => setCreateDistrictKeys((prev) => prev.filter((x) => x !== key))}
                    >
                      Remove
                    </button>
                  </li>
                ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">
              {createDistrictPickKey
                ? `${districtLabel(geoStore, createDistrictPickKey)} will be saved when you create. Use Add district if you need several districts.`
                : "Choose a district above, then create or click Add district to build a list."}
            </p>
          )}
        </>
      ) : null}

      {createScopeType === "mandal" ? (
        <>
          <p className="section-lead section-lead--tight text-muted" style={{ marginTop: "0.5rem" }}>
            Choose district and mandal, then Add mandal. Repeat for each mandal served.
          </p>
          <div className="geo-zones-add-row">
            <div className="field field-inline">
              <label htmlFor="new-sy-mandal-district">District</label>
              <SearchableSelect
                id="new-sy-mandal-district"
                variant="dash"
                value={mandalDistrict}
                onChange={(v) => {
                  if (v) {
                    setMandalDistrict(v);
                    setMandalName("");
                  }
                }}
                options={districtPickOptions}
                placeholder="District"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 12rem" }}>
              <label htmlFor="new-sy-mandal-name">Mandal</label>
              <SearchableSelect
                id="new-sy-mandal-name"
                variant="dash"
                value={mandalName}
                onChange={setMandalName}
                options={mandalPickOptions}
                placeholder="— Choose mandal —"
              />
            </div>
            <button type="button" className="btn btn--tg-secondary btn--sm" disabled={!mandalName.trim()} onClick={addCreateMandal}>
              Add mandal
            </button>
          </div>
          {createMandalRefs.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {createMandalRefs.map((r) => (
                <li key={mandalRefKey(r.district, r.mandalName)} className="geo-zones-pill-row">
                  <span>
                    <strong>{districtLabel(geoStore, r.district)}</strong> — {r.mandalName}
                  </span>
                  <button type="button" className="btn btn--link btn--sm" onClick={() => removeCreateMandal(r)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">Add at least one mandal before creating.</p>
          )}
        </>
      ) : null}

      {createScopeType === "village" ? (
        <>
          <p className="section-lead section-lead--tight text-muted" style={{ marginTop: "0.5rem" }}>
            Choose district, mandal, and village, then Add village.
          </p>
          <div className="geo-zones-add-row geo-zones-add-row--wrap">
            <div className="field field-inline">
              <label htmlFor="new-sy-village-district">District</label>
              <SearchableSelect
                id="new-sy-village-district"
                variant="dash"
                value={villageDistrict}
                onChange={(v) => {
                  if (v) {
                    setVillageDistrict(v);
                    setVillageMandal("");
                    setVillageName("");
                  }
                }}
                options={districtPickOptions}
                placeholder="District"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 11rem" }}>
              <label htmlFor="new-sy-village-mandal">Mandal</label>
              <SearchableSelect
                id="new-sy-village-mandal"
                variant="dash"
                value={villageMandal}
                onChange={(v) => {
                  setVillageMandal(v);
                  setVillageName("");
                }}
                options={villageMandalPickOptions}
                placeholder="— Choose mandal —"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 12rem" }}>
              <label htmlFor="new-sy-village-name">Village</label>
              <SearchableSelect
                id="new-sy-village-name"
                variant="dash"
                value={villageName}
                onChange={setVillageName}
                options={villageNamePickOptions}
                placeholder="— Choose village —"
              />
            </div>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              disabled={!villageMandal.trim() || !villageName.trim()}
              onClick={addCreateVillage}
            >
              Add village
            </button>
          </div>
          {createVillageRefs.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {createVillageRefs.map((r) => (
                <li key={villageRefKey(r.district, r.mandalName, r.villageName)} className="geo-zones-pill-row">
                  <span>
                    <strong>{districtLabel(geoStore, r.district)}</strong> — {r.mandalName} — {r.villageName}
                  </span>
                  <button type="button" className="btn btn--link btn--sm" onClick={() => removeCreateVillage(r)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">Add at least one village before creating.</p>
          )}
        </>
      ) : null}

      {createScopeType === "zone" ? (
        <>
          <p className="section-lead section-lead--tight text-muted" style={{ marginTop: "0.5rem" }}>
            Tick zones from <Link to="/zones">Zones master</Link>.
          </p>
          {sortedZones.length === 0 ? (
            <p className="text-muted section-lead section-lead--tight">
              No zones yet — define them under <Link to="/zones">Zones</Link> first.
            </p>
          ) : (
            <div className="geo-zones-district-grid">
              {sortedZones.map((z) => (
                <label key={z.id} className="geo-zones-check">
                  <input type="checkbox" checked={createZoneIds.includes(z.id)} onChange={() => toggleCreateZone(z.id)} />
                  <span>{z.name}</span>
                </label>
              ))}
            </div>
          )}
        </>
      ) : null}

      <button
        type="button"
        className="btn btn--primary btn--tg"
        style={{ marginTop: "1rem" }}
        disabled={!createName.trim() || !coverageOk}
        onClick={() => {
          void (async () => {
            if (
              !addStoreCoverageValid(
                createScopeType,
                effectiveDistrictKeysForCreate,
                createMandalRefs,
                createVillageRefs,
                createZoneIds,
              )
            ) {
              showToast("Select at least one district, mandal, village, or zone for the chosen coverage type.", "error");
              return;
            }
            const id = await addStoreYard({
              name: createName.trim(),
              notes: createNotes.trim(),
              address: createAddress.trim(),
            });
            if (!id) return;
            await updateStoreYard(id, {
              scopeType: createScopeType,
              districtKeys: createScopeType === "district" ? effectiveDistrictKeysForCreate : [],
              mandalRefs: createScopeType === "mandal" ? createMandalRefs : [],
              villageRefs: createScopeType === "village" ? createVillageRefs : [],
              zoneIds: createScopeType === "zone" ? createZoneIds : [],
            });
            setCreateName("");
            setCreateNotes("");
            setCreateAddress("");
            setCreateScopeType("district");
            resetCreateGeo();
            onCreated();
          })();
        }}
      >
        Create store / yard
      </button>
    </>
  );
}

function StoreYardEditorForm({
  store: s,
  geoZones,
  index,
  total,
  onDeleted,
}: {
  store: StoreYard;
  geoZones: GeoZone[];
  index: number;
  total: number;
  onDeleted?: () => void;
}) {
  const { store: geoStore } = useGeoDirectory();
  const { applications, updateStoreYard, removeStoreYard, moveStoreYard } = useAppState();
  const [name, setName] = useState(s.name);
  const [notes, setNotes] = useState(s.notes);
  const [address, setAddress] = useState(s.address ?? "");

  useEffect(() => {
    setName(s.name);
    setNotes(s.notes);
    setAddress(s.address ?? "");
  }, [s.id, s.name, s.notes, s.address]);

  const matchCount = useMemo(
    () => applications.filter((a) => applicationMatchesStoreYard(a, s, geoZones)).length,
    [applications, s, geoZones],
  );

  const [mandalDistrict, setMandalDistrict] = useState("");
  const [mandalName, setMandalName] = useState("");
  const [villageDistrict, setVillageDistrict] = useState("");
  const [villageMandal, setVillageMandal] = useState("");
  const [villageName, setVillageName] = useState("");
  const [districtPickKey, setDistrictPickKey] = useState("");

  useEffect(() => {
    if (!geoStore) return;
    const first = geoStore.tgRegistrationDistricts()[0]?.districtKey ?? "";
    setMandalDistrict((prev) => prev || first);
    setVillageDistrict((prev) => prev || first);
  }, [geoStore]);

  const mandalsForPick = useMemo(
    () => (geoStore ? geoStore.tgMandalsForDistrict(mandalDistrict) : []),
    [geoStore, mandalDistrict],
  );
  const villagesForPick = useMemo(() => {
    if (!geoStore || !villageMandal.trim()) return [];
    return geoStore.tgVillagesForMandal(villageDistrict, villageMandal);
  }, [geoStore, villageDistrict, villageMandal]);

  const sortedDistricts = useMemo(() => districtSelectOptionsFromStore(geoStore), [geoStore]);

  const districtPickOptions = sortedDistricts;

  const districtAddOptions = useMemo(() => {
    const avail = sortedDistricts.filter((d) => !s.districtKeys.includes(d.value));
    return [{ value: "", label: "— Choose district —", rowKey: "none" }, ...avail];
  }, [sortedDistricts, s.districtKeys]);

  const mandalPickOptions = useMemo(() => {
    const sorted = mandalSelectOptionsFromStore(geoStore, mandalDistrict);
    return [{ value: "", label: "— Choose mandal —", rowKey: "none" }, ...sorted];
  }, [geoStore, mandalDistrict]);

  const villageMandalPickOptions = useMemo(() => {
    const sorted = mandalSelectOptionsFromStore(geoStore, villageDistrict);
    return [{ value: "", label: "— Choose mandal —", rowKey: "none" }, ...sorted];
  }, [villageDistrict, geoStore]);

  const villageNamePickOptions = useMemo(() => {
    const sorted = villageSelectOptionsFromStore(geoStore, villageDistrict, villageMandal);
    return [{ value: "", label: "— Choose village —", rowKey: "none" }, ...sorted];
  }, [geoStore, villageDistrict, villageMandal]);

  const sortedZones = useMemo(
    () => [...geoZones].sort((a, b) => a.sortOrder - b.sortOrder),
    [geoZones],
  );

  function addDistrictPick() {
    const k = districtPickKey;
    if (!k || s.districtKeys.includes(k)) return;
    void updateStoreYard(s.id, { districtKeys: [...s.districtKeys, k] });
    setDistrictPickKey("");
  }

  function removeDistrictKey(key: string) {
    void updateStoreYard(s.id, { districtKeys: s.districtKeys.filter((dk) => dk !== key) });
  }

  function addMandal() {
    const mn = mandalName.trim();
    if (!mn) return;
    const ref: GeoZoneMandalRef = { district: mandalDistrict, mandalName: mn };
    const k = mandalRefKey(ref.district, ref.mandalName);
    if (s.mandalRefs.some((r) => mandalRefKey(r.district, r.mandalName) === k)) return;
    void updateStoreYard(s.id, { mandalRefs: [...s.mandalRefs, ref] });
    setMandalName("");
  }

  function removeMandal(ref: GeoZoneMandalRef) {
    void updateStoreYard(s.id, {
      mandalRefs: s.mandalRefs.filter(
        (r) => mandalRefKey(r.district, r.mandalName) !== mandalRefKey(ref.district, ref.mandalName),
      ),
    });
  }

  function addVillage() {
    const mn = villageMandal.trim();
    const vn = villageName.trim();
    if (!mn || !vn) return;
    const ref: GeoZoneVillageRef = { district: villageDistrict, mandalName: mn, villageName: vn };
    const k = villageRefKey(ref.district, ref.mandalName, ref.villageName);
    if (s.villageRefs.some((r) => villageRefKey(r.district, r.mandalName, r.villageName) === k)) return;
    void updateStoreYard(s.id, { villageRefs: [...s.villageRefs, ref] });
    setVillageName("");
  }

  function removeVillage(ref: GeoZoneVillageRef) {
    void updateStoreYard(s.id, {
      villageRefs: s.villageRefs.filter(
        (r) =>
          villageRefKey(r.district, r.mandalName, r.villageName) !==
          villageRefKey(ref.district, ref.mandalName, ref.villageName),
      ),
    });
  }

  function toggleZone(zoneId: string) {
    const has = s.zoneIds.includes(zoneId);
    const zoneIds = has ? s.zoneIds.filter((id) => id !== zoneId) : [...s.zoneIds, zoneId];
    void updateStoreYard(s.id, { zoneIds });
  }

  function onScopeTypeChange(next: StoreYardScopeType) {
    void updateStoreYard(s.id, {
      scopeType: next,
      districtKeys: [],
      mandalRefs: [],
      villageRefs: [],
      zoneIds: [],
    });
  }

  return (
    <div className="milestone-master-modal-editor geo-zones-card">
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 className="card__title card__title--gov" style={{ margin: 0 }}>
          Store / yard
        </h2>
        <div className="toolbar-row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn--tg-secondary btn--sm"
            disabled={index <= 0}
            onClick={() => void moveStoreYard(s.id, "up")}
          >
            Move up
          </button>
          <button
            type="button"
            className="btn btn--tg-secondary btn--sm"
            disabled={index >= total - 1}
            onClick={() => void moveStoreYard(s.id, "down")}
          >
            Move down
          </button>
          <button
            type="button"
            className="btn btn--tg-secondary btn--sm"
            onClick={() => {
              if (window.confirm(`Delete store / yard “${s.name}”?`)) {
                void (async () => {
                  const ok = await removeStoreYard(s.id);
                  if (ok) onDeleted?.();
                })();
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="field">
        <label htmlFor={`sy-name-${s.id}`}>Name</label>
        <input id={`sy-name-${s.id}`} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </div>
      <div className="field">
        <label htmlFor={`sy-notes-${s.id}`}>Notes</label>
        <textarea id={`sy-notes-${s.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </div>
      <div className="field">
        <label htmlFor={`sy-address-${s.id}`}>Shipping address (for PO print)</label>
        <textarea
          id={`sy-address-${s.id}`}
          rows={3}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          maxLength={4000}
        />
      </div>
      <button
        type="button"
        className="btn btn--primary btn--tg btn--sm"
        onClick={() => void updateStoreYard(s.id, { name, notes, address })}
      >
        Save name, notes &amp; address
      </button>

      <p className="text-muted section-lead section-lead--tight" style={{ marginTop: "1rem" }}>
        <strong>{matchCount}</strong> applicant file{matchCount === 1 ? "" : "s"} match this store’s current rule (for this console’s
        mock data).
      </p>

      <h3 className="form-section-title" style={{ marginTop: "1.15rem" }}>
        Coverage type
      </h3>
      <p className="section-lead section-lead--tight text-muted">
        Choose one level. Changing type clears the previous selection. Finer scopes (village, mandal) win over district or zone when
        resolving which store serves a beneficiary.
      </p>
      <div className="field">
        <label htmlFor={`sy-scope-${s.id}`}>How this store is tied to geography</label>
        <select
          id={`sy-scope-${s.id}`}
          value={s.scopeType}
          onChange={(e) => onScopeTypeChange(e.target.value as StoreYardScopeType)}
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <p className="section-lead section-lead--tight text-muted" style={{ marginTop: "0.4rem" }}>
        Coverage saves as soon as you change it (district list, mandals, villages, zones, or coverage type). &quot;Save name &amp; notes&quot;
        only updates the name and notes fields.
      </p>

      {s.scopeType === "district" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
            Districts served
          </h3>
          <p className="section-lead section-lead--tight text-muted">
            Add every district this store covers (multiselect). Choose a district, click Add district, repeat.
          </p>
          <div className="geo-zones-add-row">
            <div className="field field-inline" style={{ flex: "1 1 14rem" }}>
              <label htmlFor={`sy-${s.id}-district-pick`}>District</label>
              <SearchableSelect
                id={`sy-${s.id}-district-pick`}
                variant="dash"
                value={districtPickKey}
                onChange={(v) => setDistrictPickKey(v)}
                options={districtAddOptions}
                placeholder="— Choose district —"
              />
            </div>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              disabled={
                !districtPickKey ||
                !districtPickKey ||
                s.districtKeys.includes(districtPickKey)
              }
              onClick={() => void addDistrictPick()}
            >
              Add district
            </button>
          </div>
          {s.districtKeys.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {[...s.districtKeys]
                .sort((a, b) => districtLabel(geoStore, a).localeCompare(districtLabel(geoStore, b)))
                .map((key) => (
                  <li key={key} className="geo-zones-pill-row">
                    <span>{districtLabel(geoStore, key)}</span>
                    <button type="button" className="btn btn--link btn--sm" onClick={() => removeDistrictKey(key)}>
                      Remove
                    </button>
                  </li>
                ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">Add at least one district.</p>
          )}
        </>
      ) : null}

      {s.scopeType === "mandal" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
            Mandals served
          </h3>
          <div className="geo-zones-add-row">
            <div className="field field-inline">
              <label htmlFor={`sy-${s.id}-mandal-district`}>District</label>
              <SearchableSelect
                id={`sy-${s.id}-mandal-district`}
                variant="dash"
                value={mandalDistrict}
                onChange={(v) => {
                  if (v) {
                    setMandalDistrict(v);
                    setMandalName("");
                  }
                }}
                options={districtPickOptions}
                placeholder="District"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 12rem" }}>
              <label htmlFor={`sy-${s.id}-mandal-name`}>Mandal</label>
              <SearchableSelect
                id={`sy-${s.id}-mandal-name`}
                variant="dash"
                value={mandalName}
                onChange={setMandalName}
                options={mandalPickOptions}
                placeholder="— Choose mandal —"
              />
            </div>
            <button type="button" className="btn btn--tg-secondary btn--sm" disabled={!mandalName.trim()} onClick={addMandal}>
              Add mandal
            </button>
          </div>
          {s.mandalRefs.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {s.mandalRefs.map((r) => (
                <li key={mandalRefKey(r.district, r.mandalName)} className="geo-zones-pill-row">
                  <span>
                    <strong>{districtLabel(geoStore, r.district)}</strong> — {r.mandalName}
                  </span>
                  <button type="button" className="btn btn--link btn--sm" onClick={() => removeMandal(r)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">Add at least one mandal.</p>
          )}
        </>
      ) : null}

      {s.scopeType === "village" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
            Villages served
          </h3>
          <div className="geo-zones-add-row geo-zones-add-row--wrap">
            <div className="field field-inline">
              <label htmlFor={`sy-${s.id}-village-district`}>District</label>
              <SearchableSelect
                id={`sy-${s.id}-village-district`}
                variant="dash"
                value={villageDistrict}
                onChange={(v) => {
                  if (v) {
                    setVillageDistrict(v);
                    setVillageMandal("");
                    setVillageName("");
                  }
                }}
                options={districtPickOptions}
                placeholder="District"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 11rem" }}>
              <label htmlFor={`sy-${s.id}-village-mandal`}>Mandal</label>
              <SearchableSelect
                id={`sy-${s.id}-village-mandal`}
                variant="dash"
                value={villageMandal}
                onChange={(v) => {
                  setVillageMandal(v);
                  setVillageName("");
                }}
                options={villageMandalPickOptions}
                placeholder="— Choose mandal —"
              />
            </div>
            <div className="field field-inline" style={{ flex: "1 1 12rem" }}>
              <label htmlFor={`sy-${s.id}-village-name`}>Village</label>
              <SearchableSelect
                id={`sy-${s.id}-village-name`}
                variant="dash"
                value={villageName}
                onChange={setVillageName}
                options={villageNamePickOptions}
                placeholder="— Choose village —"
              />
            </div>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              disabled={!villageMandal.trim() || !villageName.trim()}
              onClick={addVillage}
            >
              Add village
            </button>
          </div>
          {s.villageRefs.length > 0 ? (
            <ul className="geo-zones-pill-list">
              {s.villageRefs.map((r) => (
                <li key={villageRefKey(r.district, r.mandalName, r.villageName)} className="geo-zones-pill-row">
                  <span>
                    <strong>{districtLabel(geoStore, r.district)}</strong> — {r.mandalName} — {r.villageName}
                  </span>
                  <button type="button" className="btn btn--link btn--sm" onClick={() => removeVillage(r)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted section-lead section-lead--tight">Add at least one village.</p>
          )}
        </>
      ) : null}

      {s.scopeType === "zone" ? (
        <>
          <h3 className="form-section-title" style={{ marginTop: "1rem" }}>
            Zones from master
          </h3>
          <p className="section-lead section-lead--tight text-muted">
            Uses the same rules as <Link to="/zones">Zones</Link>. Tick one or more zones this depot supplies.
          </p>
          {sortedZones.length === 0 ? (
            <p className="text-muted section-lead section-lead--tight">
              No zones defined yet — create them under <Link to="/zones">Zones</Link> first.
            </p>
          ) : (
            <div className="geo-zones-district-grid">
              {sortedZones.map((z) => (
                <label key={z.id} className="geo-zones-check">
                  <input type="checkbox" checked={s.zoneIds.includes(z.id)} onChange={() => toggleZone(z.id)} />
                  <span>{z.name}</span>
                </label>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export function StoresYardsPage() {
  const { storeYards, geoZones } = useAppState();
  const sorted = useMemo(() => [...storeYards].sort((a, b) => a.sortOrder - b.sortOrder), [storeYards]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalStoreId, setEditModalStoreId] = useState<string | null>(null);

  const editModalStore = useMemo(
    () => (editModalStoreId ? sorted.find((x) => x.id === editModalStoreId) : undefined),
    [editModalStoreId, sorted],
  );

  const editModalIndex = useMemo(() => {
    if (!editModalStore) return -1;
    return sorted.findIndex((s) => s.id === editModalStore.id);
  }, [editModalStore, sorted]);

  useEffect(() => {
    if (editModalStoreId && !editModalStore) setEditModalStoreId(null);
  }, [editModalStoreId, editModalStore]);

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  useEffect(() => {
    if (!createModalOpen && !editModalStoreId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCreateModal();
        setEditModalStoreId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createModalOpen, editModalStoreId, closeCreateModal]);

  const openCreateModal = () => {
    setEditModalStoreId(null);
    setCreateModalOpen(true);
  };

  const openEditModal = (id: string) => {
    setCreateModalOpen(false);
    setEditModalStoreId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="milestone-master-page">
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: "Stores / yards" },
        ]}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Stores &amp; yards
            <span className="milestone-master__catalog-count">{sorted.length}</span>
          </span>
        }
        actions={
          <button type="button" className="btn btn--primary btn--tg btn--sm" onClick={openCreateModal}>
            Add store / yard
          </button>
        }
      />

      <section
        className="milestone-master__catalog"
        aria-labelledby="stores-yards-catalog-heading"
        style={{ marginTop: "1.75rem" }}
      >

        {sorted.length === 0 ? (
          <div className="milestone-master__empty">
            <p className="milestone-master__empty-title">No stores yet</p>
            <p className="milestone-master__empty-desc">
              Create your first depot with <strong>Add store / yard</strong>, then pick districts, mandals, villages, or
              zones in the dialog.
            </p>
          </div>
        ) : (
          <div className="milestone-master-picker-grid">
            {sorted.map((s, i) => (
              <StoreYardSummaryCard
                key={s.id}
                store={s}
                storeIndex={i}
                isActive={editModalStoreId === s.id}
                onOpen={() => openEditModal(s.id)}
              />
            ))}
          </div>
        )}
      </section>

      {createModalOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            closeCreateModal();
          }}
        >
          <div
            className="modal milestone-master-modal stores-yards-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="stores-yards-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="stores-yards-create-title" className="milestone-master-modal__title">
                Add store or yard
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <AddStoreYardForm
                geoZones={geoZones}
                onCreated={() => {
                  closeCreateModal();
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {editModalStore && editModalIndex >= 0 ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            setEditModalStoreId(null);
          }}
        >
          <div
            className="modal milestone-master-modal stores-yards-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="stores-yards-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="stores-yards-edit-title" className="milestone-master-modal__title">
                Edit store / yard
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalStoreId(null)}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <StoreYardEditorForm
                key={editModalStore.id}
                store={editModalStore}
                geoZones={geoZones}
                index={editModalIndex}
                total={sorted.length}
                onDeleted={() => setEditModalStoreId(null)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
